import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft, FiUser, FiMail, FiCalendar, FiCheck, FiX } from "react-icons/fi";
import { seriesSubmissionApi, seriesQcStatus } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import RejectModal from "./RejectModal";
import ConfirmDialog from "./ConfirmDialog";
import style from "./SubmissionDetail.module.css";
import { qcTone, fmtDate, plural } from "./submissionUtils";

// Review page for a grouped series submission: a creator's new series (all
// its seasons + episodes) is approved or rejected as one item. Opened from a
// season row (?season=N) it covers that season only, matching the row. Each
// episode still links to its own detail page for a closer look.
const QC_STATUSES = ["Pending", "Approved", "Rejected"];

// Episodes grouped under season headings, seasons in number order ("Other
// episodes" last), however the API happens to order them.
const groupBySeason = (episodes) => {
  const bySeason = new Map();
  episodes.forEach((ep) => {
    const key = ep.season_number ?? null;
    if (!bySeason.has(key)) bySeason.set(key, []);
    bySeason.get(key).push(ep);
  });
  return [...bySeason.entries()]
    .sort(([a], [b]) => (a == null) - (b == null) || a - b)
    .map(([number, eps]) => ({
      key: number ?? "other",
      label: number != null ? `Season ${number}` : "Other episodes",
      episodes: eps,
    }));
};

const SeriesSubmissionDetail = () => {
  const { seriesId } = useParams();
  const [searchParams] = useSearchParams();
  // Only a known status is ever sent; anything else falls back to Pending.
  const qcParam = searchParams.get("qc");
  const qc = QC_STATUSES.includes(qcParam) ? qcParam : "Pending";
  const seasonParam = searchParams.get("season");
  const season = seasonParam && /^\d+$/.test(seasonParam) ? Number(seasonParam) : null;
  const navigate = useNavigate();
  const { can } = useRbac();
  const canDecide = can("content", "approve");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // "approve" | "reject" | null
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await seriesSubmissionApi(seriesId, qc);
      // A permission-denied read resolves with `data: []` (see axiosInstance),
      // which is not a series: show "not found" rather than an empty one.
      const series = res?.data?.data;
      setData(series && !Array.isArray(series) ? series : null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, qc]);

  // Scoped to the season when one was given: these are exactly the episodes
  // shown and the only ones a decision here is sent for.
  const episodes = (data?.episodes || []).filter(
    (ep) => season == null || Number(ep.season_number) === season
  );
  // The status the server answered for (the episodes listed are in it).
  const status = data?.qc_status || qc;
  const isPending = status === "Pending" && episodes.length > 0;
  // An approved series is live: it can be taken down from here (the list has
  // no one-click takedown for a series row).
  const isLive = status === "Approved" && episodes.length > 0;
  const seriesName = data?.name || "Untitled series";
  const pageTitle = season != null ? `${seriesName} · Season ${season}` : seriesName;
  const unit = season != null ? "season" : "series";
  const episodesLabel = plural(episodes.length, "episode");

  // Back to where the admin came from (keeps their tab); straight to the list
  // when the page was opened directly.
  const goBack = () =>
    window.history.state?.idx > 0 ? navigate(-1) : navigate("/mainlayout/submissions");

  const decide = async (payload, kind) => {
    setActing(kind);
    try {
      // Only the episodes shown here, so one uploaded mid-review isn't
      // approved unseen.
      const res = await seriesQcStatus(seriesId, {
        ...payload,
        episode_ids: episodes.map((e) => e.id),
      });
      showToast(res?.data?.message || "Series updated", "success");
      goBack();
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Failed to update series",
        "error"
      );
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className={style.page}>
        <div className={style.centerState}>Loading submission…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={style.page}>
        <button className={style.backBtn} onClick={goBack}>
          <FiArrowLeft /> Back
        </button>
        <div className={style.centerState}>Submission not found.</div>
      </div>
    );
  }

  const seasons = groupBySeason(episodes);

  return (
    <div className={style.page}>
      <button className={style.backBtn} onClick={goBack}>
        <FiArrowLeft /> Back to submissions
      </button>

      <div className={style.headerRow}>
        <div>
          <div className={style.titleLine}>
            <h1 className={style.title}>{pageTitle}</h1>
            <span className={`${style.badge} ${style[qcTone(status)]}`}>{status}</span>
          </div>
          <div className={style.subMeta}>
            <span>
              Series · {plural(seasons.length, "season")} · {episodesLabel}
            </span>
            {data.creator?.name && (
              <span>
                <FiUser size={13} /> {data.creator.name}
              </span>
            )}
            {data.submission_date && (
              <span>
                <FiCalendar size={13} /> Submitted {fmtDate(data.submission_date)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={style.grid}>
        <div className={style.mainCol}>
          {(data.landscape || data.portrait) && (
            <div className={style.playerWrap}>
              <img
                src={data.landscape || data.portrait}
                alt={seriesName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          )}

          {data.description && (
            <section className={style.section}>
              <h3 className={style.sectionTitle}>Description</h3>
              <p className={style.description}>{data.description}</p>
            </section>
          )}

          {seasons.map((season) => (
            <section className={style.section} key={season.key}>
              <h3 className={style.sectionTitle}>{season.label}</h3>
              <div className={style.people}>
                {season.episodes.map((ep) => (
                  <div
                    className={style.person}
                    key={ep.id}
                    style={{ cursor: "pointer" }}
                    title="Open episode"
                    onClick={() => navigate(`/mainlayout/submissions/${ep.id}`)}
                  >
                    <span className={style.personName}>{ep.name || "Untitled"}</span>
                    <span className={style.personRole}>
                      {ep.episode_number != null ? `Episode ${ep.episode_number}` : "Episode"}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className={style.sideCol}>
          <div className={style.card}>
            <h3 className={style.cardTitle}>Creator</h3>
            <div className={style.creatorRow}>
              <div className={style.creatorAvatar}>
                <FiUser size={18} />
              </div>
              <div>
                <div className={style.creatorName}>
                  {data.creator?.name || "Unknown creator"}
                </div>
                {data.creator?.email && (
                  <a className={style.creatorEmail} href={`mailto:${data.creator.email}`}>
                    <FiMail size={12} /> {data.creator.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className={style.actionBar}>
        <div className={style.actionInfo}>
          {!episodes.length ? (
            <span>
              This {unit} has no {status.toLowerCase()} episodes, so there is nothing to decide here.
            </span>
          ) : isPending && canDecide ? (
            <span>
              Review the {unit} above. Your decision applies to {episodes.length === 1 ? "its" : "all"}{" "}
              {episodesLabel}.
            </span>
          ) : isPending ? (
            <span>You don't have permission to approve or reject submissions.</span>
          ) : isLive && canDecide ? (
            <span>
              This {unit} is <strong className={style[qcTone(status)]}>{status}</strong> and live.
              Taking it down applies to {episodes.length === 1 ? "its" : "all"} {episodesLabel}.
            </span>
          ) : (
            <span>
              This {unit} is <strong className={style[qcTone(status)]}>{status}</strong>. Open an
              episode to act on it individually.
            </span>
          )}
        </div>
        {isPending && canDecide && (
          <div className={style.actionBtns}>
            <button
              className={style.rejectBtn}
              disabled={!!acting}
              onClick={() => setRejectOpen(true)}
            >
              <FiX size={16} /> Reject {unit}
            </button>
            <button
              className={style.approveBtn}
              disabled={!!acting}
              onClick={() => setApproveOpen(true)}
            >
              <FiCheck size={16} /> {acting === "approve" ? "Approving…" : `Approve ${unit}`}
            </button>
          </div>
        )}
        {isLive && canDecide && (
          <div className={style.actionBtns}>
            <button
              className={style.rejectBtn}
              disabled={!!acting}
              onClick={() => setRejectOpen(true)}
            >
              <FiX size={16} /> {acting === "reject" ? "Taking down…" : `Take down ${unit}`}
            </button>
          </div>
        )}
      </div>

      <RejectModal
        open={rejectOpen}
        title={`${pageTitle} (${episodesLabel})`}
        takedown={isLive}
        canRemoveTrust={isLive && !!data.creator?.is_trusted_creator}
        creatorName={data.creator?.name || ""}
        submitting={acting === "reject"}
        onCancel={() => setRejectOpen(false)}
        onConfirm={({ reason, note, notify, removeTrust }) =>
          decide(
            { status: "Rejected", reason, note, notify, remove_trust: isLive ? !!removeTrust : false },
            "reject"
          )
        }
      />

      <ConfirmDialog
        open={approveOpen}
        tone="success"
        confirmLabel="Approve"
        title={`Approve ${unit}`}
        message={`Approve “${pageTitle}” and ${
          episodes.length === 1 ? "its" : "all"
        } ${episodesLabel}? Once approved ${
          episodes.length === 1 ? "it becomes" : "they become"
        } live-ready.`}
        showNotify
        showTrustCreator={!!data.creator && !data.creator.is_trusted_creator}
        trustCreatorLabel={`Trust ${
          data.creator?.name || "this creator"
        } — publish their future content without review`}
        trustCreatorHelper={`Their later uploads and edits go live immediately instead of waiting here. This ${unit} is approved either way.`}
        submitting={acting === "approve"}
        onCancel={() => setApproveOpen(false)}
        onConfirm={({ notify, trustCreator }) =>
          decide({ status: "Approved", notify, trust_creator: !!trustCreator }, "approve")
        }
      />
    </div>
  );
};

export default SeriesSubmissionDetail;
